// components/trust/SkillVerification.jsx
import React, { useState } from 'react';
import { Upload, Camera, FileText, Award, Shield, AlertCircle, Check, Clock, X } from 'lucide-react'; // Added X import
import Modal from '../common/Modal';
import { useSkills } from '../../context/SkillContext';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';

const SkillVerification = ({ skillId, onClose, onComplete }) => {
  const { skills, verifySkill, loading } = useSkills();
  const { user } = useAuth();
  const { addNotification } = useNotification();
  
  const [verificationMethod, setVerificationMethod] = useState(null);
  const [formData, setFormData] = useState({
    description: '',
    experience: '',
    files: [],
    references: ''
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const skill = skills.find(s => s.id === skillId);
  if (!skill) return null;

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file types and sizes
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      if (!validTypes.includes(file.type)) {
        addNotification({
          type: 'error',
          title: 'Invalid file type',
          message: 'Please upload JPEG, PNG, or PDF files only',
          showToast: true
        });
        return false;
      }
      
      if (file.size > maxSize) {
        addNotification({
          type: 'error',
          title: 'File too large',
          message: 'Files must be under 5MB',
          showToast: true
        });
        return false;
      }
      
      return true;
    });
    
    setFormData(prev => ({
      ...prev,
      files: [...prev.files, ...validFiles]
    }));
  };

  const handleSubmit = async () => {
    try {
      // Validate based on tier
      if (skill.tier >= 2 && formData.files.length === 0) {
        addNotification({
          type: 'error',
          title: 'Documents required',
          message: 'Please upload verification documents',
          showToast: true
        });
        return;
      }
      
      // Simulate upload progress
      setUploadProgress(0);
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);
      
      const result = await verifySkill(skillId, {
        method: verificationMethod,
        ...formData
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Show success notification
      addNotification({
        type: 'tokens_earned',
        title: skill.tier === 1 ? 'Skill Verified!' : 'Verification Submitted!',
        message: skill.tier === 1 
          ? `${skill.name} has been verified. You earned ${skill.tokenReward} tokens!`
          : `Your ${skill.name} verification is under review. You'll be notified once approved.`,
        showToast: true
      });
      
      if (onComplete) onComplete(result);
      onClose();
      
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Verification failed',
        message: error.message,
        showToast: true
      });
    }
  };

  const renderVerificationMethod = () => {
    switch (skill.tier) {
      case 1: // Self-report
        return (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900">Self-Verification</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    This skill can be self-reported and will be instantly verified.
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Describe your experience
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Tell us about your experience with this skill..."
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Years of experience
              </label>
              <input
                type="text"
                value={formData.experience}
                onChange={(e) => setFormData({...formData, experience: e.target.value})}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="e.g., 3 years"
              />
            </div>
          </div>
        );
        
      case 2: // Document upload
        return (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-yellow-900">Document Verification</h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    Upload your certification or license for verification.
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upload documents
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600 mb-2">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-gray-500">
                  PDF, PNG, or JPEG (max 5MB)
                </p>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="mt-3 inline-block bg-blue-600 text-white px-4 py-2 rounded-lg cursor-pointer hover:bg-blue-700"
                >
                  Select Files
                </label>
              </div>
              
              {formData.files.length > 0 && (
                <div className="mt-3 space-y-2">
                  {formData.files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
                      <span className="text-sm text-gray-700">{file.name}</span>
                      <button
                        onClick={() => setFormData({
                          ...formData,
                          files: formData.files.filter((_, i) => i !== index)
                        })}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional information (optional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={2}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Any additional details..."
              />
            </div>
          </div>
        );
        
      case 3: // Professional verification
        return (
          <div className="space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Award className="w-5 h-5 text-purple-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-purple-900">Professional Verification</h4>
                  <p className="text-sm text-purple-700 mt-1">
                    This skill requires professional documentation and admin review.
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Professional license/certification
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600 mb-2">
                  Upload clear photos of your professional documents
                </p>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="prof-upload"
                />
                <label
                  htmlFor="prof-upload"
                  className="mt-3 inline-block bg-purple-600 text-white px-4 py-2 rounded-lg cursor-pointer hover:bg-purple-700"
                >
                  Upload Documents
                </label>
              </div>
              
              {formData.files.length > 0 && (
                <div className="mt-3 space-y-2">
                  {formData.files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
                      <span className="text-sm text-gray-700">{file.name}</span>
                      <button
                        onClick={() => setFormData({
                          ...formData,
                          files: formData.files.filter((_, i) => i !== index)
                        })}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Professional references
              </label>
              <textarea
                value={formData.references}
                onChange={(e) => setFormData({...formData, references: e.target.value})}
                rows={3}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Provide 2-3 professional references with contact information..."
              />
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-gray-600 mt-0.5" />
                <div className="text-sm text-gray-600">
                  <p className="font-medium">Review process</p>
                  <p>Professional verifications typically take 2-3 business days.</p>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Verify ${skill.name}`}
      size="medium"
    >
      <div className="space-y-6">
        {/* Skill Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-medium text-gray-900">{skill.name}</h4>
              <p className="text-sm text-gray-600 mt-1">{skill.description}</p>
              <div className="flex items-center gap-4 mt-2">
                <span className="text-sm text-gray-500">
                  Category: <span className="font-medium">{skill.category}</span>
                </span>
                <span className="text-sm text-gray-500">
                  Tier: <span className="font-medium">{skill.tier}</span>
                </span>
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {skill.tokenReward}
              </div>
              <div className="text-xs text-gray-500">tokens</div>
            </div>
          </div>
        </div>
        
        {/* Requirements */}
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Requirements</h4>
          <ul className="space-y-1">
            {skill.requirements.map((req, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                <Check className="w-4 h-4 text-green-600 mt-0.5" />
                <span>{req}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Trust Score Check */}
        {user?.trust?.score < skill.trustRequirement && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-red-900">Trust Score Required</h4>
                <p className="text-sm text-red-700 mt-1">
                  This skill requires a trust score of {skill.trustRequirement}+. 
                  Your current score is {user.trust.score.toFixed(1)}.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Verification Form */}
        {user?.trust?.score >= skill.trustRequirement && (
          <>
            {renderVerificationMethod()}
            
            {/* Upload Progress */}
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || (skill.tier === 1 && !formData.description)}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Verifying...' : 'Submit Verification'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default SkillVerification;